import { useEffect, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { analytics } from '@/lib/analytics';
import { supabase } from '@/integrations/supabase/client';
import { useQuery } from '@tanstack/react-query';

interface AnalyticsProviderProps {
  children: ReactNode;
}

/**
 * Global analytics provider that:
 * 1. Auto-tracks page views on route changes
 * 2. Respects GDPR consent preferences
 * 3. Initializes analytics session
 */
export function AnalyticsProvider({ children }: AnalyticsProviderProps) {
  const location = useLocation();

  // Check GDPR consent  - check if user has consented to analytics tracking
  const { data: hasConsent } = useQuery({
    queryKey: ['gdpr-consent'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return true; // Allow anonymous tracking
      
      const { data } = await supabase
        .from('gdpr_consent')
        .select('consented')
        .eq('user_id', user.id)
        .eq('consent_type', 'analytics')
        .maybeSingle();
      
      // Default to true if no record exists
      return data?.consented ?? true;
    },
  });

  // Track page views on route changes
  useEffect(() => {
    if (hasConsent) {
      analytics.trackPageView(location.pathname);
    }
  }, [location.pathname, hasConsent]);

  return <>{children}</>;
}
